import { st, classes } from './Header.st.css';
import React, { PureComponent } from 'react';
import { ChevronLeftSmall } from '@wix/wix-ui-icons-common';
import { Breadcrumbs } from '../../../Breadcrumbs';
import { Text } from '../../../Text';
import { Heading } from '../../../Heading';
import { CloseButton } from '../../../CloseButton';
import { dataHooks } from './constants';
import { Transition } from '../../../Transition';
import { WixStyleReactEnvironmentContext } from '../../../WixStyleReactEnvironmentProvider/context';
import { IconThemeContext } from '../../../WixDesignSystemIconThemeProvider/IconThemeContext';
import { HeaderProps, HeaderSkin } from './Header.types';

type HeaderBreadcrumbs = HeaderProps['breadcrumbs'];
type Title = HeaderProps['title'];
type HasBackgroundImage = HeaderProps['hasBackgroundImage'];

type DefaultProps = { minimized: boolean };
type Props = HeaderProps & DefaultProps;

const isDarkTheme = (
  hasBackgroundImage: HasBackgroundImage,
  minimized: boolean,
  skin?: HeaderSkin,
) => {
  switch (skin) {
    case 'light':
      return false;
    case 'dark':
      return true;
    default:
      return hasBackgroundImage && !minimized;
  }
};

const getBreadcrumbsTheme = (
  hasBackgroundImage: HasBackgroundImage,
  minimized: boolean,
  skin?: HeaderSkin,
) =>
  isDarkTheme(hasBackgroundImage, minimized, skin)
    ? 'onDarkBackground'
    : 'onGrayBackground';

const getTitle = (title: Title, minimized: boolean) =>
  typeof title === 'function' ? title(minimized) : title;

const generateDefaultBreadcrumbs = (
  title: Title,
  hasBackgroundImage: HasBackgroundImage,
  minimized: boolean,
  skin?: HeaderSkin,
): React.ReactElement => (
  <Breadcrumbs
    items={[{ id: '1', value: getTitle(title, minimized) }]}
    activeId="1"
    size="medium"
    theme={getBreadcrumbsTheme(hasBackgroundImage, minimized, skin)}
    onClick={() => {}}
  />
);

const getBreadcrumbs = (breadcrumbs: HeaderBreadcrumbs, minimized: boolean) =>
  typeof breadcrumbs === 'function' ? breadcrumbs(minimized) : breadcrumbs;

const generateThemedBreadcrumbs = (
  breadcrumbs: HeaderBreadcrumbs,
  title: Title,
  hasBackgroundImage: HasBackgroundImage,
  minimized: boolean,
  skin?: HeaderSkin,
) => {
  if (breadcrumbs) {
    // @ts-expect-error
    return React.cloneElement(getBreadcrumbs(breadcrumbs, minimized), {
      theme: getBreadcrumbsTheme(hasBackgroundImage, minimized, skin),
    });
  }

  return generateDefaultBreadcrumbs(title, hasBackgroundImage, minimized);
};

/**
 * A header that sticks at the top of the container which minimizes on scroll
 */
class Header extends PureComponent<
  Props,
  { themedBreadcrumbs: React.ReactElement }
> {
  constructor(props: Props) {
    super(props);

    const { breadcrumbs, title, hasBackgroundImage, minimized, skin } = props;
    this.state = {
      themedBreadcrumbs: generateThemedBreadcrumbs(
        breadcrumbs,
        title,
        hasBackgroundImage,
        minimized,
        skin,
      ),
    };
  }
  context!: React.ContextType<typeof WixStyleReactEnvironmentContext>;
  static displayName = 'Page.Header';
  static defaultProps: DefaultProps = {
    minimized: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { breadcrumbs, title, hasBackgroundImage, minimized, skin } =
      this.props;
    const newBreadcrumbs = nextProps.breadcrumbs;
    const newTitle = nextProps.title;
    const newHasBackgroundImage = nextProps.hasBackgroundImage;
    const newMinimized = nextProps.minimized;
    const newSkin = nextProps.skin;

    if (
      breadcrumbs !== newBreadcrumbs ||
      title !== newTitle ||
      hasBackgroundImage !== newHasBackgroundImage ||
      minimized !== newMinimized ||
      skin !== newSkin
    ) {
      const themedBreadcrumbs = generateThemedBreadcrumbs(
        newBreadcrumbs,
        newTitle,
        newHasBackgroundImage,
        newMinimized,
        skin,
      );
      this.setState({ themedBreadcrumbs });
    }
  }

  _animateComponent = (
    show: boolean | undefined,
    useEnterDelay: boolean,
    content: React.ReactNode,
  ) => {
    if (show) {
      return content;
    }

    return useEnterDelay ? (
      <Transition
        show={show}
        enterAnimation={{
          fadeIn: {
            duration: 'medium01',
            delay: 'short',
          },
        }}
        exitAnimation={{
          fadeOut: true,
        }}
      >
        {content}
      </Transition>
    ) : (
      <Transition
        show={show}
        enterAnimation={{
          fadeIn: true,
        }}
        exitAnimation={{
          fadeOut: true,
        }}
      >
        {content}
      </Transition>
    );
  };

  render() {
    const {
      dataHook,
      breadcrumbs,
      onBackClicked,
      title,
      subtitle,
      minimized,
      skin,
      actionsBar,
      showBackButton,
      hasBackgroundImage,
      className,
      backButtonAriaLabel,
    } = this.props;

    const breadcrumbsExists = !!breadcrumbs;
    const { themedBreadcrumbs } = this.state;
    const _title = getTitle(title, minimized);
    const isDark = isDarkTheme(hasBackgroundImage, minimized, skin);
    const { pageHeaderId } = this.context;

    const hasBackButton = !!showBackButton && !!onBackClicked;

    const titleHidden = minimized && breadcrumbsExists;

    const titleMinimised = minimized && !breadcrumbsExists;

    const backButtonIcon = onBackClicked && (
      <IconThemeContext.Consumer>
        {({ icons }) => {
          const BackIcon = (icons.PageHeader?.ChevronLeftSmall ||
            ChevronLeftSmall) as typeof ChevronLeftSmall;

          return (
            <CloseButton
              className={classes.backButton}
              skin="standardFilled"
              dataHook={dataHooks.backButton}
              onClick={onBackClicked}
              aria-label={backButtonAriaLabel}
            >
              <BackIcon className={classes.backButtonIcon} />
            </CloseButton>
          );
        }}
      </IconThemeContext.Consumer>
    );

    return (
      <div className={st(classes.root, {}, className)} data-hook={dataHook}>
        <div className={classes.header}>
          <div>
            {breadcrumbsExists && (
              <div
                className={classes.breadcrumbsContainer}
                data-hook={dataHooks.breadcrumbs}
              >
                {themedBreadcrumbs}
              </div>
            )}
          </div>

          <div
            className={st(classes.titleContainer, {
              hidden: titleHidden,
              minimised: titleMinimised,
            })}
          >
            <div
              className={st(classes.titleRow, {
                withBackButton: hasBackButton,
              })}
            >
              {hasBackButton &&
                this._animateComponent(
                  !titleHidden,
                  !breadcrumbsExists,
                  backButtonIcon,
                )}

              {title &&
                this._animateComponent(
                  !titleHidden,
                  !breadcrumbsExists,
                  <Heading
                    size="extraLarge"
                    className={st(classes.title)}
                    dataHook={dataHooks.title}
                    ellipsis={typeof _title === 'string'}
                    light={isDark}
                    id={pageHeaderId}
                  >
                    {_title}
                  </Heading>,
                )}
            </div>

            {subtitle &&
              this._animateComponent(
                !minimized,
                !breadcrumbsExists,
                <div
                  data-hook={dataHooks.subtitle}
                  className={st(classes.subtitleWrapper)}
                >
                  <Text
                    className={st(classes.subtitle)}
                    ellipsis={typeof subtitle === 'string'}
                    light={isDark}
                    secondary={!isDark}
                    maxLines={2}
                    maxWidth="288px"
                  >
                    {subtitle}
                  </Text>
                </div>,
              )}
          </div>
        </div>
        {actionsBar && (
          <div
            className={st(classes.actionsBar, {
              minimized,
              withBreadcrumbs: breadcrumbsExists,
            })}
            data-hook={dataHooks.actionBar}
          >
            {typeof actionsBar === 'function'
              ? actionsBar({
                  minimized,
                  hasBackgroundImage,
                  skin,
                })
              : actionsBar}
          </div>
        )}
      </div>
    );
  }
}

Header.contextType = WixStyleReactEnvironmentContext;

export { Header as PageHeader };
